import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, MessageCircle, Phone } from "lucide-react";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/shared/SEO";
import WhatsAppFAB from "@/components/shared/WhatsAppFAB";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/context/I18nContext";
import { api, unwrap } from "@/lib/api";

export default function ContactPage() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [whatsapp, setWhatsapp] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    api.get("/public/settings")
      .then((r) => {
        const d = unwrap(r) || {};
        setWhatsapp(d.whatsapp_number || "");
        setPhone(d.company_phone || d.support_phone || "");
      })
      .catch(() => {});
  }, []);

  const waDigits = (whatsapp || "").replace(/[^0-9]/g, "");
  const waUrl = waDigits
    ? `https://wa.me/${waDigits}?text=${encodeURIComponent("Hi! I have a question about Ever Clean Home.")}`
    : "";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="contact-page">
      <SEO
        title="Contact us — Ever Clean Home"
        description="Talk to a real human at Ever Clean Home. Questions about booking, recurring service, or a custom cleaning quote? Send us a message."
      />
      <Header />
      <WhatsAppFAB />
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-16">
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">{t("contact.title")}</h1>
        <p className="mt-2 text-[#4B5563]">{t("contact.subtitle")}</p>
        {phone && (
          <a href={`tel:${phone.replace(/[^0-9+]/g, "")}`} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#0F766E] hover:text-[#0EA5A4]" data-testid="contact-phone">
            <Phone className="w-4 h-4" /> {phone}
          </a>
        )}

        {/* WhatsApp direct channel — hidden when no number is configured */}
        {waUrl && (
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 flex items-center justify-between gap-4 rounded-2xl border border-[#25D366]/30 bg-[#25D366]/10 p-5 hover:bg-[#25D366]/15 transition-colors group"
            data-testid="contact-whatsapp-box"
          >
            <div className="flex items-center gap-4">
              <span className="w-12 h-12 rounded-xl bg-[#25D366] text-white grid place-items-center shrink-0">
                <MessageCircle className="w-6 h-6" fill="currentColor" strokeWidth={0} />
              </span>
              <div>
                <div className="font-display font-bold text-[#0C1A1A]">{t("contact.whatsapp_title")}</div>
                <div className="text-sm text-[#4B5563]">{t("contact.whatsapp_desc")}</div>
              </div>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#25D366] text-white text-sm font-semibold shrink-0 group-hover:scale-[1.03] transition-transform" data-testid="contact-whatsapp-cta">
              {t("contact.whatsapp_cta")} →
            </span>
          </a>
        )}

        {waUrl && (
          <div className="mt-8 mb-2 text-xs uppercase tracking-wider text-[#9CA3AF] font-semibold" data-testid="contact-or-form">
            {t("contact.or_form")}
          </div>
        )}

        <Card className={`rounded-2xl p-6 ${waUrl ? "mt-2" : "mt-8"} border border-[#E5E7EB]`}>
          {sent ? (
            <div className="text-center py-8" data-testid="contact-sent">
              <div className="text-[#10B981] font-display text-2xl font-bold">{t("contact.sent")}</div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-sm">{t("contact.name")}</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="rounded-xl mt-1" data-testid="contact-name" />
              </div>
              <div>
                <Label className="text-sm">{t("contact.email")}</Label>
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="rounded-xl mt-1" data-testid="contact-email" />
              </div>
              <div>
                <Label className="text-sm">{t("contact.message")}</Label>
                <Textarea rows={6} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="rounded-xl mt-1" data-testid="contact-message" />
              </div>
              <Button type="submit" disabled={submitting}
                className="w-full h-12 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-semibold" data-testid="contact-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> {t("contact.send")}</>}
              </Button>
            </form>
          )}
        </Card>
      </div>
      <Footer />
    </div>
  );
}
